import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_luxury_sage_010',
  name: 'Luxury Sage Botanical',
  occasionType: 'BABY_SHOWER',
  description: 'A luxury botanical baby shower invitation featuring a soft sage green watercolor background with eucalyptus branches, white roses, and an elegant watercolor baby bassinet.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'sage', 'botanical', 'roses', 'bassinet'],
  colorScheme: { primary: '#064e3b', accent: '#ca8a04', bg: '#f0fdf4' },
};

export function LuxurySageBotanical({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Charlotte Sophia';
  const hostedBy = baby.hostedBy || data.hostName || 'Sarah & David Sophia';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'JANUARY 17, 2027';
  let dayOfWeek = 'SUNDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '02:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'THE BOTANIST GARDENS';
  const venueAddress = baby.venueAddress || data.eventVenue || '321 Magnolia Boulevard, Pasadena, CA';
  const rsvpName = baby.rsvpName || 'Jessica';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 019-2834';
  const message = baby.message || 'Help us welcome our sweet little one with warm hearts and blessings.';
  const dressCode = baby.dressCode || 'Sage green, soft eucalyptus, or white';

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-botanical { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-botanical { font-family: 'Montserrat', sans-serif; }
        .font-cursive-botanical { font-family: 'Pinyon Script', cursive; }
        .font-title-botanical { font-family: 'Cinzel', serif; }
        
        .sage-bg-watercolor {
          background: radial-gradient(circle at 50% 50%, #ffffff 0%, #f4fbf7 40%, #e6f7ed 70%, #d1fae5 100%);
          position: relative;
        }
        
        /* Shimmer Overlay */
        .gold-shimmer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(251,191,36,0.1) 30%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.3) 100%);
          mix-blend-mode: overlay;
          z-index: 2;
        }

        /* Fine Paper Grain Texture */
        .paper-grain-sage {
          position: absolute;
          inset: 0;
          pointer-events: none;
          mix-blend-mode: multiply;
          opacity: 0.16;
          z-index: 1;
        }

        .gold-leaf-text {
          background: linear-gradient(135deg, #a16207 0%, #d97706 40%, #eab308 60%, #ca8a04 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.9));
        }

        .green-sparkle-pt {
          position: absolute;
          width: 5px;
          height: 5px;
          background-color: rgba(16, 185, 129, 0.4);
          border-radius: 50%;
          filter: blur(0.5px);
          animation: floatSparkle 5s infinite ease-in-out;
        }
        @keyframes floatSparkle {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.2; }
          50% { transform: translateY(-10px) scale(1.2); opacity: 0.7; }
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[540px] aspect-[1080/1920] shadow-2xl rounded-2xl overflow-hidden sage-bg-watercolor flex flex-col p-10 border-[5px] border-white/85">
        {/* Shimmer & Paper Texture Layers */}
        <div className="gold-shimmer" />
        <svg className="paper-grain-sage" width="100%" height="100%">
          <filter id="linenNoise">
            <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.05 0" />
            <feComposite operator="in" in2="SourceGraphic" />
          </filter>
          <rect width="100%" height="100%" filter="url(#linenNoise)" />
        </svg>

        {/* Scattered Sparkles */}
        <div className="green-sparkle-pt top-[20%] left-[10%]" />
        <div className="green-sparkle-pt top-[35%] right-[12%]" />
        <div className="green-sparkle-pt top-[70%] left-[15%]" />
        <div className="green-sparkle-pt top-[85%] right-[8%]" />

        {/* Thin Gold Border Frame */}
        <div className="absolute inset-4 border border-dashed border-[#ca8a04]/20 rounded-2xl pointer-events-none z-10" />
        <div className="absolute inset-5 border-2 border-[#ca8a04]/30 rounded-2xl pointer-events-none z-10">
          <div className="absolute top-[-2px] left-[-2px] w-6 h-6 border-t border-l border-[#ca8a04]" />
          <div className="absolute top-[-2px] right-[-2px] w-6 h-6 border-t border-r border-[#ca8a04]" />
          <div className="absolute bottom-[-2px] left-[-2px] w-6 h-6 border-b border-l border-[#ca8a04]" />
          <div className="absolute bottom-[-2px] right-[-2px] w-6 h-6 border-b border-r border-[#ca8a04]" />
        </div>

        {/* Watercolor Baby Bassinet decorated with flowers */}
        <div className="absolute top-8 inset-x-0 h-44 pointer-events-none z-10 flex justify-center">
          <svg viewBox="0 0 160 120" className="w-36 h-full">
            <path d="M 65 95 L 80 75 L 95 95 M 80 75 L 80 55" stroke="#ca8a04" strokeWidth="1.25" fill="none" />
            <path d="M 50 40 Q 80 30, 110 40 Q 120 60, 80 70 Q 40 60, 50 40 Z" fill="none" stroke="#ca8a04" strokeWidth="1.5" />
            <path d="M 55 44 Q 80 35, 105 44" stroke="#d97706" strokeWidth="0.5" fill="none" opacity="0.6" />
            <path d="M 60 52 Q 80 43, 100 52" stroke="#d97706" strokeWidth="0.5" fill="none" opacity="0.6" />
            <path d="M 70 60 Q 80 53, 90 60" stroke="#d97706" strokeWidth="0.5" fill="none" opacity="0.6" />
            <path d="M 52 41 Q 80 34, 108 41 Q 112 55, 80 62 Q 48 55, 52 41 Z" fill="#fffbeb" opacity="0.9" />
            <circle cx="50" cy="40" r="4.5" fill="#fef3c7" />
            <circle cx="50" cy="40" r="2.5" fill="#fff" />
            <circle cx="110" cy="40" r="4.5" fill="#fef3c7" />
            <circle cx="110" cy="40" r="2.5" fill="#fff" />
            <path d="M 45 45 Q 40 55, 42 60" fill="#047857" opacity="0.8" />
            <path d="M 115 45 Q 120 55, 118 60" fill="#047857" opacity="0.8" />
          </svg>
        </div>

        {/* Eucalyptus, Olive branches, White roses framing surrounding the edges */}
        <div className="absolute top-[-5px] left-[-5px] w-32 h-32 pointer-events-none z-10 opacity-95">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path d="M 15 15 Q 35 25, 42 45 Q 32 35, 15 15" fill="#065f46" opacity="0.75" />
            <path d="M 15 15 Q 25 35, 45 42 Q 35 32, 15 15" fill="#047857" opacity="0.7" />
            <circle cx="20" cy="20" r="9" fill="#fff" stroke="#fef08a" strokeWidth="0.5" />
            <circle cx="20" cy="20" r="6" fill="#fffbeb" />
            <path d="M 25 8 Q 35 4, 38 14" stroke="#ca8a04" strokeWidth="0.75" fill="none" />
          </svg>
        </div>

        <div className="absolute bottom-[-10px] right-[-10px] w-36 h-36 pointer-events-none z-10 opacity-95">
          <svg viewBox="0 0 100 100" className="w-full h-full transform rotate-180">
            <path d="M 15 15 Q 35 25, 42 45 Q 32 35, 15 15" fill="#065f46" opacity="0.75" />
            <path d="M 15 15 Q 25 35, 45 42 Q 35 32, 15 15" fill="#047857" opacity="0.7" />
            <circle cx="20" cy="20" r="10" fill="#fff" stroke="#fef08a" strokeWidth="0.5" />
            <circle cx="20" cy="20" r="7" fill="#fffbeb" />
            <path d="M 25 8 Q 35 4, 38 14" stroke="#ca8a04" strokeWidth="0.75" fill="none" />
          </svg>
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-48 pb-12 px-4">
          
          {/* Header section */}
          <div className="text-center">
            <p className="font-title-botanical text-[8.5px] tracking-[0.48em] text-[#064e3b]/60 uppercase mb-8">
              JOIN US TO CELEBRATE A
            </p>

            <h2 className="font-title-botanical text-[4rem] text-[#064e3b] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-1 gold-leaf-text">
              BABY
            </h2>
            <h2 className="font-title-botanical text-[4rem] text-[#064e3b] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-9 gold-leaf-text">
              SHOWER
            </h2>

            <svg viewBox="0 0 220 12" className="w-44 h-3 mx-auto mb-8" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#064e3b" strokeWidth="0.9" opacity="0.45"/>
              <circle cx="110" cy="6" r="2.8" fill="#064e3b" opacity="0.55"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#064e3b" strokeWidth="0.9" opacity="0.45"/>
            </svg>

            <span className="font-title-botanical text-[8.5px] text-[#ca8a04]/80 tracking-[0.42em] block mb-8 uppercase">
              IN HONOR OF
            </span>

            <h1 className="font-cursive-botanical text-[5.8rem] text-[#064e3b] leading-none mb-9 gold-leaf-text">
              {motherName}
            </h1>

            <svg viewBox="0 0 220 12" className="w-36 h-3 mx-auto mb-5" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#064e3b" strokeWidth="0.8" opacity="0.40"/>
              <circle cx="110" cy="6" r="2.2" fill="#064e3b" opacity="0.48"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#064e3b" strokeWidth="0.8" opacity="0.40"/>
            </svg>
          </div>

          {/* Details Section */}
          <div className="flex flex-col items-center gap-5 text-center font-sans-botanical flex-grow justify-center">
            
            <div className="w-full max-w-[360px] mx-auto">
              <div className="border-t border-b border-[#064e3b]/30 py-4 flex items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-botanical text-[7px] tracking-[0.32em] text-[#064e3b]/60 uppercase block mb-1">DAY</span>
                  <span className="font-title-botanical text-[9px] tracking-wider text-[#064e3b] font-semibold block">{dayOfWeek}</span>
                  <span className="font-serif-botanical text-[11.5px] text-stone-800 font-semibold block mt-0.5">{formattedDate}</span>
                </div>
                <div className="w-px h-10 bg-[#064e3b]/20 mx-5 shrink-0" />
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-botanical text-[7px] tracking-[0.32em] text-[#064e3b]/60 uppercase block mb-1">TIME</span>
                  <span className="font-title-botanical text-[9px] tracking-wider text-[#064e3b] font-semibold block">AT</span>
                  <span className="font-serif-botanical text-[11.5px] text-stone-800 font-semibold block mt-0.5">{eventTime}</span>
                </div>
              </div>
            </div>

            <div className="w-full max-w-[340px] mx-auto text-center">
              <span className="font-title-botanical text-[7px] tracking-[0.35em] text-[#064e3b]/60 uppercase block mb-1.5">VENUE</span>
              <span className="font-title-botanical text-[10px] tracking-[0.18em] text-[#064e3b] font-semibold block uppercase">{venueName}</span>
              <span className="font-sans-botanical text-[9px] text-stone-600 block leading-relaxed mt-1">{venueAddress}</span>
            </div>

            <div
              role="button"
              aria-label="RSVP Now"
              onClick={() => setShowRsvpModal(true)}
              className="cursor-pointer group"
            >
              <div
                className="px-9 py-2.5 rounded-full border border-[#064e3b]/30 bg-gradient-to-r from-[#f0fdf4] via-white to-[#f0fdf4] group-hover:from-[#dcfce7] group-hover:to-[#dcfce7] transition-all duration-200"
                style={{ boxShadow: '0 2px 14px rgba(6,78,59,0.06)' }}
              >
                <span className="font-title-botanical text-[8.5px] tracking-[0.38em] text-[#064e3b] font-semibold uppercase block text-center">RSVP</span>
                <span className="font-sans-botanical text-[9.5px] text-stone-700 font-medium block text-center mt-0.5">{rsvpName} · {rsvpPhone}</span>
              </div>
            </div>

            {dressCode && (
              <div
                className="inline-block px-5 py-1.5 rounded-full border border-[#064e3b]/30"
                style={{ background: 'rgba(240,253,244,0.60)' }}
              >
                <span className="font-title-botanical text-[7.5px] tracking-[0.28em] text-[#064e3b] uppercase font-semibold">
                  DRESS CODE &ensp;·&ensp; {dressCode}
                </span>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className="text-center pt-4">
            <span className="font-serif-botanical text-[10px] text-stone-600 block leading-relaxed max-w-[300px] mx-auto mb-3">
              "{message}"
            </span>

            <svg viewBox="0 0 180 10" className="w-36 h-2 mx-auto mb-3" fill="none">
              <line x1="0" y1="5" x2="72" y2="5" stroke="#064e3b" strokeWidth="0.7" opacity="0.4"/>
              <circle cx="90" cy="5" r="2" fill="#064e3b" opacity="0.5"/>
              <line x1="108" y1="5" x2="180" y2="5" stroke="#064e3b" strokeWidth="0.7" opacity="0.4"/>
            </svg>

            <span className="font-serif-botanical text-[11px] text-[#064e3b]/80 block tracking-wide">
              Hosted with love by
            </span>
            <span className="font-title-botanical text-[9.5px] tracking-[0.28em] text-stone-800 font-semibold block mt-1 uppercase">
              {hostedBy}
            </span>
          </div>

        </div>

      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: LuxurySageBotanical });
